import React, {FC, useEffect, useMemo, useState} from 'react'
import type {Vessel} from '../../Management/core/_models'
import type {
  QHSEAuditFindingDto,
  QHSEInspectionFindingDto,
  SafetyCommitteeFindingLinkType,
} from '../core/_models'
import {
  searchAuditFindings,
  searchInspectionFindings,
  deleteSafetyCommitteeAttachment,
  type SafetyCommitteeAttachmentType,
} from '../core/_requests'
import SearchableSelect from '../../../shared/components/SearchableSelect'

type CompanyGroup = {id: number; name: string}
type Subcompany = {id: number; name: string; companyId: number}

type AttachmentItem = {name: string}

type SCMRecord = {
  id: number
  vesselId: number | null
  scmName: string
  dateHeld: string
  findingName: string
  findingsLinkType: SafetyCommitteeFindingLinkType | ''
  findingId?: number | null
  presentSafetyOfficer: string
  designatedSafetyOfficer: string
  mastersRemarks: string
  officeRemarks: string
  attachedForm: AttachmentItem[]
  checklistAttachments: AttachmentItem[]
  findingAttachments: AttachmentItem[]
  officeAttachments: AttachmentItem[]
}

type SCMFormState = Omit<SCMRecord, 'id'>

type SCMUploadFiles = {
  attachedForm: File[]
  checklistAttachments: File[]
  findingAttachments: File[]
  officeAttachments: File[]
}

type SCMExistingAttachments = {
  attachedForm: AttachmentItem[]
  checklistAttachments: AttachmentItem[]
  findingAttachments: AttachmentItem[]
  officeAttachments: AttachmentItem[]
}

const emptyUploads: SCMUploadFiles = {
  attachedForm: [],
  checklistAttachments: [],
  findingAttachments: [],
  officeAttachments: [],
}

const emptyExistingAttachments: SCMExistingAttachments = {
  attachedForm: [],
  checklistAttachments: [],
  findingAttachments: [],
  officeAttachments: [],
}

const emptyForm: SCMFormState = {
  vesselId: null,
  scmName: '',
  dateHeld: '',
  findingName: '',
  findingsLinkType: '',
  findingId: null,
  presentSafetyOfficer: '',
  designatedSafetyOfficer: '',
  mastersRemarks: '',
  officeRemarks: '',
  attachedForm: [],
  checklistAttachments: [],
  findingAttachments: [],
  officeAttachments: [],
}

const attachmentsTooltip = (items: AttachmentItem[] | undefined | null) => {
  if (!items || items.length === 0) return ''
  return items.map(i => i.name).join(', ')
}

export type SCMFormModalProps = {
  visible: boolean
  title: string
  scmId?: number
  initial?: SCMFormState
  /** Vessel-assigned user layout (sections); not authorization. */
  shipboardUser: boolean
  /** Field editability from effective permissions (parent computes via SAFETY_COMMITTEE_UPDATE, etc.). */
  canEditMastersRemarks: boolean
  canEditOfficeRemarks: boolean
  vessels: Vessel[]
  companies: CompanyGroup[]
  subcompanies: Subcompany[]
  isTopLevel: boolean
  effectiveCompanyId?: number
  defaultVesselId?: number
  onClose: () => void
  onSave: (data: SCMFormState, uploads: SCMUploadFiles) => void
  onSubmit: (data: SCMFormState, uploads: SCMUploadFiles) => void
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

export const SCMFormModal: FC<SCMFormModalProps> = ({
  visible,
  title,
  scmId,
  initial,
  shipboardUser,
  canEditMastersRemarks,
  canEditOfficeRemarks,
  vessels,
  companies,
  subcompanies,
  isTopLevel,
  effectiveCompanyId,
  defaultVesselId,
  onClose,
  onSave,
  onSubmit,
}) => {
  const isAddMode = !scmId
  const [formData, setFormData] = useState<SCMFormState>(initial || emptyForm)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [companyId, setCompanyId] = useState<string>('')
  const [subcompanyId, setSubcompanyId] = useState<string>('')
  const [findingOptions, setFindingOptions] = useState<Array<{id: number; label: string}>>([])
  const [findingLoading, setFindingLoading] = useState(false)
  const [existingAttachments, setExistingAttachments] = useState<SCMExistingAttachments>(
    emptyExistingAttachments
  )
  const [uploadFiles, setUploadFiles] = useState<SCMUploadFiles>(emptyUploads)
  const [deletingAttachment, setDeletingAttachment] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (!visible) return
    setFormData(prev => ({
      ...(initial || emptyForm),
      vesselId:
        initial?.vesselId ??
        prev.vesselId ??
        (defaultVesselId ?? null),
    }))
    setErrors({})
    setExistingAttachments({
      attachedForm: initial?.attachedForm || [],
      checklistAttachments: initial?.checklistAttachments || [],
      findingAttachments: initial?.findingAttachments || [],
      officeAttachments: initial?.officeAttachments || [],
    })
    setUploadFiles(emptyUploads)

    setCompanyId(prev => {
      if (prev) return prev
      if (!isTopLevel && effectiveCompanyId) return String(effectiveCompanyId)
      return prev || ''
    })
    setSubcompanyId('')
  }, [visible, initial, defaultVesselId, isTopLevel, effectiveCompanyId])

  useEffect(() => {
    if (!visible) return
    if (!formData.vesselId) return
    const v = vessels.find(vv => Number(vv.id) === Number(formData.vesselId)) as any
    if (!v) return
    const cgaId = v.companyGroupAdmin?.id ?? v.companyGroupId ?? v.cgaid?.id ?? null
    const caId = v.companyAdmin?.id ?? v.companyId ?? null
    if (cgaId != null) setCompanyId(String(cgaId))
    if (caId != null) setSubcompanyId(String(caId))
  }, [visible, formData.vesselId, vessels])

  useEffect(() => {
    if (!visible) return
    const linkType = formData.findingsLinkType
    if (!linkType || linkType === 'MONTHLY') {
      setFindingOptions([])
      setFindingLoading(false)
      return
    }

    const vId = formData.vesselId ? Number(formData.vesselId) : undefined
    const params: any = {
      vesselId: vId,
      companyGroupId: !vId && companyId ? Number(companyId) : undefined,
      companyId: !vId && subcompanyId ? Number(subcompanyId) : undefined,
    }

    let cancelled = false
    const load = async () => {
      try {
        setFindingLoading(true)
        const toOption = (d: any) => ({
          id: Number(d.id),
          label:
            d.findingLabel ||
            d.findingName ||
            (d.description ? d.description.replace(/<[^>]*>/g, '').trim().substring(0, 60) : '') ||
            `Finding #${d.id}`,
        })

        if (linkType === 'AUDIT') {
          const list = (await searchAuditFindings(params)) as QHSEAuditFindingDto[]
          if (!cancelled) {
            const arr = Array.isArray(list) ? list : (list as any)?.content || []
            setFindingOptions(arr.map(toOption))
          }
        } else if (linkType === 'INSPECTION') {
          const list = (await searchInspectionFindings(params)) as QHSEInspectionFindingDto[]
          if (!cancelled) {
            const arr = Array.isArray(list) ? list : (list as any)?.content || []
            setFindingOptions(arr.map(toOption))
          }
        }
      } catch (e) {
        console.error('Failed to load finding options for SCM', e)
        if (!cancelled) setFindingOptions([])
      } finally {
        if (!cancelled) setFindingLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [visible, formData.findingsLinkType, formData.vesselId, companyId, subcompanyId])

  const vesselsForModal = useMemo(() => {
    let list = vessels
    if (companyId) {
      const cid = Number(companyId)
      list = list.filter((v: any) =>
        Number(v.companyGroupAdmin?.id ?? v.companyGroupId) === cid
      )
    }
    if (subcompanyId) {
      const scid = Number(subcompanyId)
      list = list.filter((v: any) =>
        Number(v.companyAdmin?.id ?? v.companyId) === scid
      )
    }
    return list
  }, [vessels, companyId, subcompanyId])

  const subcompaniesForCompany = useMemo(() => {
    if (!companyId) return []
    const cid = Number(companyId)
    return subcompanies.filter(sc => sc.companyId === cid)
  }, [companyId, subcompanies])

  useEffect(() => {
    if (!visible) return
    setFormData(prev => {
      if (!prev.vesselId) return prev
      const exists = vesselsForModal.some(v => Number(v.id) === Number(prev.vesselId))
      return exists ? prev : {...prev, vesselId: null}
    })
  }, [visible, vesselsForModal])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const {name, value} = e.target
    setFormData(prev => {
      if (name === 'findingsLinkType') {
        return {
          ...prev,
          findingsLinkType: value as SCMFormState['findingsLinkType'],
          findingId: null,
          findingName: '',
        }
      }
      return {...prev, [name]: value}
    })
    if (errors[name]) {
      setErrors(prev => ({...prev, [name]: ''}))
    }
  }

  const handleFilesChange = (name: keyof SCMUploadFiles, files: FileList | null) => {
    const list = files ? Array.from(files) : []
    setUploadFiles(prev => ({...prev, [name]: list}))
    const items = list.map(f => ({name: f.name}))
    setFormData(prev => ({
      ...prev,
      [name]: [...existingAttachments[name], ...items],
    }))
  }

  const attachmentTypeFor = (key: keyof SCMUploadFiles): SafetyCommitteeAttachmentType => {
    switch (key) {
      case 'attachedForm':
        return 'FORM'
      case 'checklistAttachments':
        return 'CHECKLIST'
      case 'findingAttachments':
        return 'FINDING'
      case 'officeAttachments':
        return 'OFFICE'
      default:
        return 'FORM'
    }
  }

  const removeAttachment = async (key: keyof SCMUploadFiles, fileName: string) => {
    const isNew = uploadFiles[key].some(f => f.name === fileName)
    if (isNew) {
      setUploadFiles(prev => ({
        ...prev,
        [key]: prev[key].filter(f => f.name !== fileName),
      }))
      setFormData(prev => ({
        ...prev,
        [key]: (prev as any)[key]?.filter((x: any) => x?.name !== fileName) ?? [],
      }))
      return
    }

    const isExisting = existingAttachments[key].some(x => x.name === fileName)
    if (!isExisting || !scmId) return

    const token = `${key}:${fileName}`
    setDeletingAttachment(prev => ({...prev, [token]: true}))
    try {
      await deleteSafetyCommitteeAttachment(scmId, attachmentTypeFor(key), fileName)
      setExistingAttachments(prev => ({
        ...prev,
        [key]: prev[key].filter(x => x.name !== fileName),
      }))
      setFormData(prev => ({
        ...prev,
        [key]: (prev as any)[key]?.filter((x: any) => x?.name !== fileName) ?? [],
      }))
    } catch (e) {
      console.error('Failed to delete attachment', e)
    } finally {
      setDeletingAttachment(prev => ({...prev, [token]: false}))
    }
  }

  const renderAttachmentChips = (key: keyof SCMUploadFiles) => {
    const items: AttachmentItem[] = (formData as any)[key] || []
    if (!items.length) return <div className='text-muted small mt-1'>No files</div>

    return (
      <div className='d-flex flex-wrap gap-2 mt-2'>
        {items.map((it) => {
          const token = `${key}:${it.name}`
          const busy = Boolean(deletingAttachment[token])

          return (
            <span
              key={it.name}
              className='badge text-dark'
              style={{background: '#f1f1f2', fontWeight: 500}}
              title={it.name}
            >
              {it.name}
              {!busy && (
                <span
                  role='button'
                  tabIndex={0}
                  aria-label={`Remove ${it.name}`}
                  className='ms-2'
                  style={{cursor: 'pointer', color: '#d9214e', fontWeight: 700}}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    removeAttachment(key, it.name)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      removeAttachment(key, it.name)
                    }
                  }}
                >
                  ×
                </span>
              )}
              {busy && <span className='ms-2 text-muted'>…</span>}
            </span>
          )
        })}
      </div>
    )
  }

  const validate = (data: SCMFormState) => {
    const next: Record<string, string> = {}
    if (!data.vesselId) next.vesselId = 'Vessel is required'
    if (!data.dateHeld) next.dateHeld = 'Date held is required'
    if (!data.scmName || !data.scmName.trim()) next.scmName = 'SCM name is required'
    if (!data.findingsLinkType) next.findingsLinkType = 'Meeting type is required'
    if (data.findingsLinkType === 'MONTHLY') {
      if (!data.findingName || !data.findingName.trim()) {
        next.findingName = 'Finding name is required'
      }
    } else if (data.findingsLinkType && !data.findingId) {
      next.findingId = 'Linked finding is required'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSave = () => {
    if (!validate(formData)) return
    onSave(formData, uploadFiles)
  }

  const handleSubmit = () => {
    if (!validate(formData)) return
    onSubmit(formData, uploadFiles)
  }

  if (!visible) return null

  const WRAP_STYLE: React.CSSProperties = {
    background: 'rgba(0,0,0,0.5)',
    position: 'fixed',
    inset: 0,
    zIndex: 1050,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '70vw',
          height: '70vh',
          maxWidth: '1100px',
          maxHeight: '800px',
        }}
      >
        {/* Header */}
        <div className='d-flex align-items-center justify-content-between border-bottom px-5 py-4 flex-shrink-0'>
          <h5 className='modal-title text-dark m-0'>{title}</h5>
          <button type='button' className='btn-close m-0' onClick={onClose} />
        </div>

        {/* Body with inner scroll */}
        <div className='flex-grow-1 overflow-auto px-5 py-4'>
          <div className='row g-4'>
            {!shipboardUser && (
              <>
                {/* Company - only show for superadmin/operator */}
                {isTopLevel && (
                  <div className='col-md-4'>
                    <label className={LABEL}>Company</label>
                    <SearchableSelect
                      className='form-select'
                      value={companyId}
                      onChange={e => {
                        setCompanyId(e.target.value)
                        setSubcompanyId('')
                      }}
                    >
                      <option value=''>All Companies</option>
                      {companies.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                )}

                {!!companyId && subcompaniesForCompany.length > 0 && (
                  <div className='col-md-4'>
                    <label className={LABEL}>Subcompany</label>
                    <SearchableSelect
                      className='form-select'
                      value={subcompanyId}
                      onChange={e => setSubcompanyId(e.target.value)}
                    >
                      <option value=''>All Subcompanies</option>
                      {subcompaniesForCompany.map(sc => (
                        <option key={sc.id} value={sc.id}>
                          {sc.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                )}

                <div className='col-md-4'>
                  <label className={LABEL}>
                    Vessel <span style={{color: '#dc3545'}}>*</span>
                  </label>
                  <SearchableSelect
                    className={`form-select ${errors.vesselId ? 'is-invalid' : ''}`}
                    name='vesselId'
                    value={formData.vesselId ?? ''}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        vesselId: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                  >
                    <option value=''>Select Vessel</option>
                    {vesselsForModal.map(v => (
                      <option key={v.id} value={v.id}>
                        {(v as any).fleet_name || v.name || `Vessel ${v.id}`}
                      </option>
                    ))}
                  </SearchableSelect>
                  {errors.vesselId && <div className='invalid-feedback'>{errors.vesselId}</div>}
                </div>
              </>
            )}

            {shipboardUser && !isAddMode && (
              <div className='col-md-4'>
                <label className={LABEL}>
                  Vessel <span style={{color: '#dc3545'}}>*</span>
                </label>
                <div className='form-control' style={{background: '#f8f9fa'}}>
                  {(vesselsForModal.find(v => Number(v.id) === Number(formData.vesselId)) as any)
                    ?.fleet_name ||
                    vesselsForModal.find(v => Number(v.id) === Number(formData.vesselId))?.name ||
                    (vessels.find(v => Number(v.id) === Number(formData.vesselId)) as any)
                      ?.fleet_name ||
                    vessels.find(v => Number(v.id) === Number(formData.vesselId))?.name ||
                    '-'}
                </div>
              </div>
            )}

            <div className='col-md-4'>
              <label className={LABEL}>SCM Name <span style={{color: '#dc3545'}}>*</span></label>
              <input
                type='text'
                className={`form-control ${errors.scmName ? 'is-invalid' : ''}`}
                name='scmName'
                value={formData.scmName}
                onChange={handleInputChange}
                placeholder='e.g. Monthly Safety Committee Meeting'
                required
                aria-required={true}
              />
              {errors.scmName && <div className='invalid-feedback'>{errors.scmName}</div>}
            </div>

            <div className='col-md-4'>
              <label className={LABEL}>
                Date Held <span style={{color: '#dc3545'}}>*</span>
              </label>
              <input
                type='date'
                className={`form-control ${errors.dateHeld ? 'is-invalid' : ''}`}
                name='dateHeld'
                value={formData.dateHeld}
                onChange={handleInputChange}
              />
              {errors.dateHeld && <div className='invalid-feedback'>{errors.dateHeld}</div>}
            </div>

            <div className='col-md-4'>
              <label className={LABEL}>Meeting Type <span style={{color: '#dc3545'}}>*</span></label>
              <SearchableSelect
                className={`form-select text-dark ${errors.findingsLinkType ? 'is-invalid' : ''}`}
                name='findingsLinkType'
                value={formData.findingsLinkType}
                onChange={handleInputChange}
                required
                aria-required={true}
              >
                <option value=''>Select Type</option>
                <option value='AUDIT'>Audit</option>
                <option value='INSPECTION'>Incident</option>
                <option value='MONTHLY'>Monthly</option>
              </SearchableSelect>
              {errors.findingsLinkType && (
                <div className='invalid-feedback'>{errors.findingsLinkType}</div>
              )}
            </div>

            {formData.findingsLinkType === 'MONTHLY' && (
              <div className='col-md-4'>
                <label className={LABEL}>
                  Finding Name <span style={{color: '#dc3545'}}>*</span>
                </label>
                <input
                  type='text'
                  className={`form-control ${errors.findingName ? 'is-invalid' : ''}`}
                  name='findingName'
                  value={formData.findingName || ''}
                  onChange={handleInputChange}
                  placeholder='Enter finding name'
                />
                {errors.findingName && <div className='invalid-feedback'>{errors.findingName}</div>}
              </div>
            )}

            {formData.findingsLinkType && formData.findingsLinkType !== 'MONTHLY' && (
              <div className='col-md-4'>
                <label className={LABEL}>
                  Finding Name <span style={{color: '#dc3545'}}>*</span>
                </label>
                <SearchableSelect
                  className={`form-select ${errors.findingId ? 'is-invalid' : ''}`}
                  value={formData.findingId ?? ''}
                  onChange={e => {
                    const nextId = e.target.value ? Number(e.target.value) : null
                    const selected = findingOptions.find(o => o.id === nextId)
                    setFormData(prev => ({
                      ...prev,
                      findingId: nextId,
                      findingName: selected?.label || prev.findingName,
                    }))
                    if (errors.findingId) {
                      setErrors(prev => ({...prev, findingId: ''}))
                    }
                  }}
                  disabled={findingLoading}
                >
                  <option value=''>
                    {findingLoading ? 'Loading findings...' : 'Select Finding'}
                  </option>
                  {findingOptions.map(opt => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.findingId && <div className='invalid-feedback'>{errors.findingId}</div>}
                {formData.findingsLinkType && !findingLoading && findingOptions.length === 0 && (
                  <div className='form-text text-muted'>No findings found for the selected scope.</div>
                )}
              </div>
            )}

            <div className='col-md-6'>
              <label className={LABEL}>Present Safety Officer</label>
              <input
                type='text'
                className='form-control'
                name='presentSafetyOfficer'
                value={formData.presentSafetyOfficer}
                onChange={handleInputChange}
              />
            </div>

            <div className='col-md-6'>
              <label className={LABEL}>Designated Safety Officer</label>
              <input
                type='text'
                className='form-control'
                name='designatedSafetyOfficer'
                value={formData.designatedSafetyOfficer}
                onChange={handleInputChange}
              />
            </div>

            <div className='col-md-6'>
              <label className={LABEL}>Attach Checklists</label>
              <input
                type='file'
                className='form-control'
                multiple
                onChange={e => handleFilesChange('checklistAttachments', e.target.files)}
              />
              {renderAttachmentChips('checklistAttachments')}
            </div>

            <div className='col-md-6'>
              <label className={LABEL}>Attach Findings</label>
              <input
                type='file'
                className='form-control'
                multiple
                onChange={e => handleFilesChange('findingAttachments', e.target.files)}
              />
              {renderAttachmentChips('findingAttachments')}
            </div>

            <div className='col-md-6'>
              <label className={LABEL}>Attached Form</label>
              <input
                type='file'
                className='form-control'
                multiple
                onChange={e => handleFilesChange('attachedForm', e.target.files)}
              />
              {renderAttachmentChips('attachedForm')}
            </div>

            <div className='col-md-6'>
              <label className={LABEL}>Office Attachment</label>
              <input
                type='file'
                className='form-control'
                multiple
                onChange={e => handleFilesChange('officeAttachments', e.target.files)}
              />
              {renderAttachmentChips('officeAttachments')}
            </div>

            <div className='col-12'>
              <label className={LABEL}>Master&apos;s / Ship&apos;s Remarks</label>
              <textarea
                className='form-control'
                name='mastersRemarks'
                rows={3}
                value={formData.mastersRemarks}
                onChange={handleInputChange}
                disabled={!canEditMastersRemarks}
                style={!canEditMastersRemarks ? {backgroundColor: '#f8f9fa', cursor: 'not-allowed'} : undefined}
                title={!canEditMastersRemarks ? 'Only Master rank crew or Super Admin can edit this field' : undefined}
              />
            </div>

            <div className='col-12'>
              <label className={LABEL}>Office Remarks</label>
              <textarea
                className='form-control'
                name='officeRemarks'
                rows={3}
                value={formData.officeRemarks}
                onChange={handleInputChange}
                disabled={!canEditOfficeRemarks}
                style={!canEditOfficeRemarks ? {backgroundColor: '#f8f9fa', cursor: 'not-allowed'} : undefined}
                title={!canEditOfficeRemarks ? 'Only office staff can edit this field' : undefined}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='border-top d-flex justify-content-end gap-2 px-5 py-4 flex-shrink-0'>
          <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
            Cancel
          </button>
          <button type='button' className='btn btn-secondary btn-sm' onClick={handleSave}>
            Save &amp; Close
          </button>
          <button type='button' className='btn btn_primary btn-sm' onClick={handleSubmit}>
            Submit &amp; Close
          </button>
        </div>
      </div>
    </div>
  )
}
